import React, { useRef, useEffect, useState } from 'react';

import { keyBy } from 'lodash';
import { isIndicatorOrConstraintBetter, isIndicatorOrConstraintWorse, isRiskOrOpportunityBetter, isRiskOrOpportunityWorse } from 'utils/rules';
import { stakesMargin as baseMargin, circlePlacementDx, circlePlacementDy, circleSpacer, textDx, textDy } from '../RiskScatterFullChart.functions';

const isSelected = (pointsList: string[], pointId) => {
    if (!pointsList) return false;
    return pointsList.find(p => p.id == pointId);
}

const sizes = {
    "0": 1,
    "1": 3,
    "2": 5,
    "3": 7,
    0: 1,
    1: 3,
    2: 5,
    3: 7
}
const DEFAULT_SIZE = 3;

const getAllInitials = (name: string) => {
    const clearedName = name ? name.replace(/[^a-zA-Z0-9]/g, ' ').replace(/(\s)+/, ' ') : '';
    return clearedName.split(' ').slice(0, 2).map((n) => (n[0] ? n[0].toUpperCase() : '') + (n[1] ? n[1] : '') + (n[2] ? n[2] : '')).join('')
}

const StakesPointsCloudInner = (props: any) => {
    // Track previous data IDs to detect newly appearing elements
    const prevDataIdsRef = useRef<Set<string> | null>(null);
    const prevDataRef = useRef<Map<string, any>>(new Map());
    const [appearingIds, setAppearingIds] = useState<Set<string>>(new Set());
    const appearingTimerRef = useRef<NodeJS.Timeout | null>(null);

    const hasSomeElementsSelected = props.isSelectedMode || props.selectedPoints.length > 0

    const { data } = props;

    useEffect(() => {
        const currentIds = new Set(data.map((d: any) => d.id));
        const prevIds = prevDataIdsRef.current;

        // Skip animation on first render - just initialize the ref
        if (prevIds === null) {
            prevDataIdsRef.current = currentIds;
            // Store initial data for potential future disappearing animations
            const dataMap = new Map();
            data.forEach((d: any) => dataMap.set(d.id, d));
            prevDataRef.current = dataMap;
            return;
        }

        // Find new IDs that weren't in the previous render
        const newAppearingIds = new Set<string>();
        currentIds.forEach(id => {
            if (!prevIds.has(id)) {
                newAppearingIds.add(id);
            }
        });

        // Update previous IDs ref and data map
        prevDataIdsRef.current = currentIds;
        const dataMap = new Map();
        data.forEach((d: any) => dataMap.set(d.id, d));
        prevDataRef.current = dataMap;

        // Handle appearing items - clear previous timer first
        if (appearingTimerRef.current) {
            clearTimeout(appearingTimerRef.current);
        }
        
        if (newAppearingIds.size > 0) {
            setAppearingIds(newAppearingIds);
            appearingTimerRef.current = setTimeout(() => {
                setAppearingIds(new Set());
            }, 1500);
        }

        return () => {
            if (appearingTimerRef.current) {
                clearTimeout(appearingTimerRef.current);
            }
        };
    }, [data]);

    // Use similarItems from parent - required for proper link coordination
    const similarStakes = props.similarItems;

    const isRecentlyCreated = (id: string): boolean => {
        return props.recentCreations && props.recentCreations[id]
    }

    const isAppearing = (id: string): boolean => {
        return appearingIds.has(id);
    }

    const margin = { top: 30, right: 30, bottom: 40, left: 40 },
        width = props.width - margin.left - margin.right

    return (

        <g className={`wrapper ${hasSomeElementsSelected ? "selectionMode" : ""}`} >

            {
                data.map((d, index) => {
                    // Skip rendering if element doesn't exist in similarStakes (needed for proper link coordination)
                    if (!similarStakes || !similarStakes[d.id]) return null;

                    // Must use similarStakes[d.id] to get the enriched object with correct index
                    const similarItem = similarStakes[d.id];
                    
                    const isWorse = isIndicatorOrConstraintWorse(d);
                    const isBetter = isIndicatorOrConstraintBetter(d);
                    const stakeTextDY = textDy(similarItem, similarStakes, props.width, baseMargin)
                    const selected = isSelected(props.selectedPoints, d.id)

                    // Calculate offset for similar items - same formula as in stakeBandXY
                    const offsetX = circleSpacer(similarItem.similarCount) * (props.width * baseMargin) * circlePlacementDx(similarItem, similarStakes);
                    const offsetY = circleSpacer(similarItem.similarCount) * (props.width * baseMargin) * circlePlacementDy(similarItem, similarStakes);

                    return <g key={d.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (props.onClick) props.onClick(d)
                        }}
                        onMouseOver={() => {
                            if (props.onHover) props.onHover(d)
                        }}
                        onMouseOut={() => {
                            if (props.onHover) props.onHover(null)
                        }}
                        style={{
                            // Include offset in transform to match link position calculations exactly
                            transform: `translate(${props.x(d.trend) + offsetX}px, ${offsetY}px)`,
                            transition: "transform 1.8s ease-in-out"
                        }}

                        className={`stake ${selected ? "selected" : ""} ${isRecentlyCreated(d.id) ? "recentlyCreated" : ""} ${isAppearing(d.id) ? "appearing" : ""}`}
                    >
                        <foreignObject className="aura" width={200} height={200}>
                            <div></div>
                        </foreignObject>
                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={sizes[d.weight] || DEFAULT_SIZE}
                            fill={isSelected(props.selectedPoints, d.id) ? "orange" : isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={isSelected(props.selectedPoints, d.id) ? "4" : isWorse ? "6" :
                                isBetter ? "6" :
                                    "6"
                            }
                            stroke={isSelected(props.selectedPoints, d.id) ? "orange" : isWorse && d.isOpportunity ? "#00000044" : isWorse && !d.isOpportunity ? "#FFFFFF55" :
                                isBetter && d.isOpportunity ? "#FFFFFF55" : isBetter && !d.isOpportunity ? "#00000044" :
                                    "#00000001"
                            }

                            style={{
                                transition: isSelected(props.selectedPoints, d.id) ? "fill 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >
                        </circle>

                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={sizes[d.weight] || DEFAULT_SIZE}
                            fill={isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={isWorse ? "1" :
                                isBetter ? "1" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "#00000001"
                            }
                            style={{
                                transition: isSelected(props.selectedPoints, d.id) ? "stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >
                        </circle>

                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarStakes, props.width, baseMargin) - offsetX}
                            dy={(stakeTextDY > 0 ? stakeTextDY + (d.weight || 1) : stakeTextDY - (d.weight || 1) - 1) - offsetY}
                            fill={isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" : "#00000077"}
                            strokeWidth={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF55" :
                                isBetter ? "#FFFFFF55" :
                                    "#00000001"
                            }
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarStakes) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarStakes) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarStakes) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {d.reference || getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>

                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarStakes, props.width, baseMargin) - offsetX}
                            dy={(stakeTextDY > 0 ? stakeTextDY + (d.weight || 1) : stakeTextDY - (d.weight || 1) - 1) - offsetY}
                            fill={"#000000"}
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarStakes) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarStakes) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarStakes) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {d.reference || getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>



                    </g>

                })
            }
        </g >





    );
};

// React.memo (cf. StakeholdersPointsCloud) — ignore les callbacks.
const propsEqual = (prev: any, next: any) => {
    return (
        prev.data === next.data &&
        prev.similarItems === next.similarItems &&
        prev.selectedPoints === next.selectedPoints &&
        prev.isSelectedMode === next.isSelectedMode &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.scenario === next.scenario &&
        prev.recentCreations === next.recentCreations &&
        prev.margin === next.margin &&
        prev.x === next.x &&
        prev.y === next.y
    );
};

export const StakesPointsCloud = React.memo(StakesPointsCloudInner, propsEqual);
